import React from 'react';
import type { UnifiedQuote } from '@/types/unified-quote';
import { Badge } from '@/components/ui/badge';
import { Clock, Mail, Share2 } from 'lucide-react';
import { currencyService } from '@/services/CurrencyService';
import { formatAmountWithFinancialPrecision } from '@/utils/quoteCurrencyUtils';

interface CompactQuoteListItemProps {
  quote: UnifiedQuote;
  onQuoteClick?: (quoteId: string) => void;
  isSelected?: boolean;
  showActions?: boolean;
}

export const CompactQuoteListItem: React.FC<CompactQuoteListItemProps> = ({
  quote,
  onQuoteClick,
  isSelected,
  showActions = true,
}) => {
  // currencyService is already imported as instance
  
  const handleClick = () => {
    if (onQuoteClick && quote.id) {
      onQuoteClick(quote.id);
    }
  };

  // Get the appropriate currency - prefer origin currency for admin view
  const currency = quote.origin_currency || 'USD';
  const amount = quote.origin_total_amount || quote.final_total_origincurrency || 0;

  return (
    <div
      className={`p-4 sm:p-6 border rounded-lg cursor-pointer hover:bg-gray-50 touch-manipulation min-h-[60px] ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
      onClick={handleClick}
    >
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="font-medium text-gray-900 text-base sm:text-lg">
              Quote #{quote.id?.slice(-8) || 'Unknown'}
            </h3>
            <div className="flex flex-wrap gap-1">
              {(quote as any).email_sent && (
                <Badge variant="outline" className="text-green-600 text-xs">
                  <Mail className="mr-1 h-3 w-3" />
                  <span className="hidden sm:inline">Sent</span>
                </Badge>
              )}
              {(quote as any).has_share_token && (
                <Badge variant="outline" className="text-blue-600 text-xs">
                  <Share2 className="mr-1 h-3 w-3" />
                  <span className="hidden sm:inline">Shared</span>
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{quote.items?.length || 0} items</span> •
              <span className="capitalize ml-1">{quote.status || 'Unknown status'}</span>
            </p>

            <div className="flex flex-wrap gap-1">
              {quote.items?.some((item: any) => item.customer_notes) && (
                <Badge variant="outline" className="text-xs">
                  <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="hidden sm:inline">Has Notes</span>
                  <span className="sm:hidden">Notes</span>
                </Badge>
              )}

              {/* Weight Revision Badge for Admin */}
              {(() => {
                const weightRevision = (quote as any).calculation_data?.customer_revisions?.weight_revision;
                if (weightRevision) {
                  const isItemLevel = weightRevision.update_mode === 'individual' && weightRevision.updated_item_name;
                  return (
                    <Badge variant="outline" className="text-amber-600 text-xs bg-amber-50 border-amber-200">
                      <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="m2 17 10 5 10-5" />
                        <path d="m2 12 10 5 10-5" />
                      </svg>
                      <span className="hidden sm:inline">
                        {isItemLevel
                          ? `${weightRevision.updated_item_name}: Weight Updated`
                          : `Weight: ${weightRevision.original_quoted_weight}kg → ${weightRevision.customer_revised_weight}kg`
                        }
                      </span>
                      <span className="sm:hidden">Weight Updated</span>
                    </Badge>
                  );
                }
                return null;
              })()}
            </div>

            {(quote as any).expiry_status && (
              <div>
                <Badge
                  variant={(quote as any).expiry_status.variant as any}
                  className="text-xs"
                >
                  <Clock className="mr-1 h-3 w-3" />
                  {(quote as any).expiry_status.text}
                </Badge>
              </div>
            )}
          </div>
        </div>

        <div className="text-left sm:text-right sm:flex-shrink-0">
          <p className="font-bold text-gray-900 text-lg sm:text-xl">
            {formatAmountWithFinancialPrecision(amount, currency)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {quote.customer_data?.info?.name || 'Unknown'}
          </p>
        </div>
      </div>
    </div>
  );
};